/*
 * ColorPark case customiser.
 *
 * One state machine owns the whole full-screen root:
 *
 *   idle -> warming -> adding -> editing -> saving -> complete
 *                                       \-> abandoned
 *                                       \-> recoverable_error
 *
 * Every visual change goes through setState, so the root can never end up
 * showing a black full-screen shell with a hidden iframe inside it. If you add
 * behaviour here, drive it from data-state rather than toggling elements.
 *
 * The ordering in finish() matters and is not arbitrary. The shopper sees the
 * Shopify saving layer BEFORE the cart write is attempted, and sees success
 * only after the cart has been re-read and verified. A design that reached the
 * printer but not the cart line is an order nobody can fulfil, so a spinner
 * that resolves into a retry is the honest outcome, never a completion screen.
 */
if (!customElements.get('colorpark-customise')) {
  customElements.define(
    'colorpark-customise',
    class ColorparkCustomise extends HTMLElement {
      connectedCallback() {
        this.cfg = {
          goodsId: this.dataset.goodsId,
          machineId: this.dataset.machineId,
          editorUrl: this.dataset.editorUrl,
          returnUrl: this.dataset.returnUrl,
          cartAdd: this.dataset.cartAddUrl,
          cartChange: this.dataset.cartChangeUrl,
          abandon: this.dataset.abandon || 'remove',
          language: this.dataset.language || 'en-us',
          saveTimeout: (parseInt(this.dataset.saveTimeout, 10) || 30) * 1000,
          // Blank until the app proxy is deployed, in which case the interim
          // theme-asset callback is used instead. Setting it is the switch, and
          // clearing it is the way back if the proxy misbehaves.
          proxyPath: (this.dataset.proxyPath || '').replace(/\/+$/, ''),
        };

        this.button = this.querySelector('[data-cp-button]');
        this.errorEl = this.querySelector('[data-cp-error]');
        this.variantId = this.dataset.variantId;
        this.variantAvailable = this.dataset.variantAvailable === 'true';

        // The root is moved out to <body> so no transformed or clipped product
        // container can trap a position:fixed layer. Once moved it is never
        // recreated, only re-stated, which is what keeps the warm preload warm.
        this.root = document.getElementById(this.dataset.rootId);
        document.body.appendChild(this.root);
        this.frame = this.root.querySelector('[data-cp-frame]');
        this.closeBtn = this.root.querySelector('[data-cp-close]');
        this.retryBtn = this.root.querySelector('[data-cp-retry]');
        this.state = 'idle';
        this.session = null;
        this.frameReady = false;

        this.onClick = this.onClick.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onRetry = this.onRetry.bind(this);
        this.onMessage = this.onMessage.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.button.addEventListener('click', this.onClick);
        this.closeBtn.addEventListener('click', this.onClose);
        this.retryBtn?.addEventListener('click', this.onRetry);
        window.addEventListener('message', this.onMessage);
        document.addEventListener('keydown', this.onKeydown);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            this.onVariantChange(event?.data?.variant);
          });
        }

        this.init();
      }

      /**
       * Pick up a design that a reload interrupted, before warming.
       *
       * Without this, reloading mid design left the pending line in the cart,
       * lost the session, and made the next press of Customise add a SECOND
       * line. The shopper ended up with two undesigned cases and, once checkout
       * validation is live, no way to check out and no obvious way to fix it.
       *
       * Recovery has to happen before warm(), because warm() mints the design
       * token that the server session is opened against. Recovering afterwards
       * would leave the cart line on the old token and the server record on the
       * new one, which quietly breaks order reconciliation.
       */
      async init() {
        await this.restoreSession();
        this.warm();
      }

      async restoreSession() {
        let stored = null;
        try {
          stored = JSON.parse(sessionStorage.getItem(`colorpark:${this.cfg.goodsId}`) || 'null');
        } catch (error) {
          stored = null;
        }
        if (!stored?.token) return;

        // The cart is the authority, not the browser. A line the shopper
        // removed by hand, or a cart that expired, must not be resurrected.
        let line = null;
        try {
          const cart = await this.fetchCart();
          line = cart.items.find((i) => i.properties?._colorpark_design_token === stored.token) || null;
        } catch (error) {
          return; // cart unreadable, leave the stored session for the next load
        }

        if (!line || line.properties?._colorpark_status === 'submitted') {
          this.clearSession();
          return;
        }

        // Key comes from the cart, not from storage, because it changes
        // whenever properties change.
        this.session = { token: stored.token, lineKey: line.key, variantId: String(line.variant_id) };
        this.pendingToken = stored.token;
        this.syncLabel();
        this.track('session_recovered');
      }

      /**
       * One place decides what the button says, so a recovered design cannot
       * leave "Finish your design" showing after it has been completed or
       * abandoned.
       */
      syncLabel() {
        const label = this.querySelector('[data-cp-label]');
        if (!label) return;
        if (!this.variantAvailable) label.textContent = this.dataset.soldOutLabel;
        else if (this.session?.lineKey) label.textContent = this.dataset.resumeLabel;
        else label.textContent = this.dataset.customiseLabel;
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.closeBtn?.removeEventListener('click', this.onClose);
        this.retryBtn?.removeEventListener('click', this.onRetry);
        window.removeEventListener('message', this.onMessage);
        document.removeEventListener('keydown', this.onKeydown);
        this.unsubscribeVariant?.();
        this.clearWatchdog();
        this.root?.remove();
      }

      /* ---------------------------------------------------------------- state */

      setState(next) {
        this.state = next;
        this.root.dataset.state = next;

        const editing = next === 'editing';
        const busy = editing || next === 'saving' || next === 'complete' || next === 'recoverable_error';

        // Never rely on the iframe merely being hidden: while it is not the
        // active surface it must also be unreachable by pointer and by tab.
        this.frame.setAttribute('aria-hidden', editing ? 'false' : 'true');
        this.frame.tabIndex = editing ? 0 : -1;
        this.root.setAttribute('aria-hidden', busy ? 'false' : 'true');

        this.lockBackground(busy);
        this.track(`state_${next}`);
      }

      lockBackground(locked) {
        if (locked === this.backgroundLocked) return;
        this.backgroundLocked = locked;

        if (locked) {
          this.scrollY = window.scrollY;
          document.body.style.top = `-${this.scrollY}px`;
          document.body.classList.add('colorpark-locked');
          document.querySelectorAll('body > *:not(.colorpark-root)').forEach((el) => {
            if (el.inert !== undefined) el.inert = true;
          });
        } else {
          document.body.classList.remove('colorpark-locked');
          document.body.style.top = '';
          document.querySelectorAll('body > *:not(.colorpark-root)').forEach((el) => {
            if (el.inert !== undefined) el.inert = false;
          });
          if (this.scrollY != null) window.scrollTo(0, this.scrollY);
        }
      }

      /* --------------------------------------------------------------- warming */

      async warm() {
        if (this.frame.dataset.loaded) return;
        if (navigator.connection?.saveData) return; // respect Save-Data, load on demand
        this.frame.dataset.loaded = 'true'; // claim it now: opening a session awaits

        // Warming is a background concern and must never pull the root out of a
        // state the shopper is currently reading. The re-warm scheduled by
        // resetFrame lands about half a second after the completion screen
        // appears, so setting 'warming' unconditionally here made that screen
        // flash up and vanish before anyone could press View cart, and released
        // the background scroll lock underneath it.
        //
        // Loading the iframe itself is still fine in any state: it is hidden
        // while complete, so the next design is warm by the time it is needed.
        if (this.state === 'idle') this.setState('warming');
        this.track('preload_started');
        this.frame.addEventListener(
          'load',
          () => {
            this.frameReady = true;
            this.track('preload_ready');
          },
          { once: true }
        );

        // The design token is minted here rather than on click because the
        // jumpUrl is baked into the warmed iframe and the server session has to
        // exist before ColorPark can redirect to it. Warming a session the
        // shopper never uses costs one short-lived record, which is cheaper
        // than losing the preload.
        this.pendingToken = this.randomId();
        try {
          this.frame.src = await this.editorSrc(this.pendingToken);
        } catch (error) {
          delete this.frame.dataset.loaded; // let a later click retry
        }
      }

      /**
       * Build the editor URL, including where ColorPark sends the shopper next.
       *
       * Whichever callback is in play, the jumpUrl must carry NO query string.
       * ColorPark builds its redirect as `jumpUrl + "?queue_id=" + id` without
       * checking for an existing query, so anything we put there comes back
       * with a second "?" and the queue ID parses as part of our own value.
       * Not theoretical: it stranded a real design on the printer.
       */
      async editorSrc(token) {
        this.nonce = this.randomId();
        this.sessionId = null;

        if (this.cfg.proxyPath) {
          // App proxy. The session ID goes in the PATH, because the query has
          // to stay free for ColorPark's append and a fragment would never
          // reach the server that has to look the session up.
          const sessionId = await this.openSession(token);
          if (sessionId) {
            this.sessionId = sessionId;
            this.callbackOrigin = window.location.origin;
            const proxied = new URL(`${this.cfg.proxyPath}/return/${sessionId}`, window.location.origin);
            return this.editorUrlWith(proxied);
          }
          // Falling through is deliberate: a proxy that is down should degrade
          // to the interim callback rather than take the customiser with it.
          this.track('session_open_failed');
        }

        // Interim theme-asset callback. No server record, and it lands in an
        // opaque origin, so the session values ride in the fragment instead.
        const asset = new URL(this.cfg.returnUrl, window.location.href);
        this.callbackOrigin = asset.origin;
        // ColorPark decodes jumpUrl and then truncates it at the first literal
        // ampersand. A fragment such as `nonce=a&parent=b` therefore arrives at
        // the callback with the nonce but without the parent origin, and the
        // callback cannot safely post back to Shopify. Keep both values inside
        // one URL-safe base64 field so there is no delimiter for ColorPark to
        // strip. A page that was already warmed with revision 2 still points at
        // the old cached callback and must be reloaded before submitting.
        asset.hash = `context=${this.callbackContext(this.nonce)}`;
        return this.editorUrlWith(asset);
      }

      editorUrlWith(returnUrl) {
        return `${this.cfg.editorUrl}&lang=${encodeURIComponent(this.cfg.language)}&jumpUrl=${encodeURIComponent(returnUrl.toString())}`;
      }

      async openSession(token) {
        try {
          const res = await fetch(`${this.cfg.proxyPath}/session`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({
              design_token: token,
              goods_id: this.cfg.goodsId,
              machine_id: this.cfg.machineId,
              variant_id: this.variantId,
              storefront_origin: window.location.origin,
            }),
          });
          if (!res.ok) return null;
          const body = await res.json();
          return typeof body.session_id === 'string' ? body.session_id : null;
        } catch (error) {
          return null;
        }
      }

      randomId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `cp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }

      callbackContext(nonce) {
        // nonce and origin are ASCII, so btoa is sufficient and avoids pulling
        // a UTF-8 codec into the storefront bundle.
        return window
          .btoa(JSON.stringify({ nonce, parent: window.location.origin }))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      }

      /* ---------------------------------------------------------------- adding */

      async onClick() {
        if (this.button.getAttribute('aria-disabled') === 'true') return;
        if (this.state === 'editing' || this.state === 'saving') return;

        // An existing pending session means the shopper came back to finish a
        // design, so reopen rather than adding a second line to the cart.
        if (this.session && this.session.lineKey) {
          this.setState('editing');
          this.focusEditor();
          return;
        }

        if (!this.variantAvailable) {
          this.showError(this.dataset.soldOutMessage);
          return;
        }

        this.track('customise_clicked');
        this.setState('adding');
        this.setBusy(true);
        this.clearError();

        // The token minted during warming, so the cart line matches the server
        // session already baked into the editor's jumpUrl. Only mint a fresh
        // one if warming never ran, e.g. under Save-Data.
        if (!this.pendingToken) await this.warm();
        const token = this.pendingToken || this.randomId();

        try {
          const res = await fetch(this.cfg.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: [
                {
                  id: Number(this.variantId),
                  quantity: 1,
                  properties: {
                    _colorpark_design_token: token,
                    _colorpark_goods_id: this.cfg.goodsId,
                    _colorpark_machine_id: this.cfg.machineId,
                    _colorpark_status: 'pending',
                  },
                },
              ],
            }),
          });

          const result = await res.json().catch(() => ({}));
          if (!res.ok || result.status) {
            throw new Error(result.description || result.message || this.dataset.errorMessage);
          }

          // Multi-item add returns the created lines under items. Reading
          // result.key here yields undefined, which silently disables every
          // later cart write and makes a successful screen a lie.
          const lineKey = result.items?.[0]?.key;
          if (!lineKey) throw new Error(this.dataset.errorMessage);

          this.session = { token, lineKey, variantId: this.variantId };
          this.persistSession();
          this.track('cart_line_created');
          this.publishCartUpdate();

          this.setBusy(false);
          this.setState('editing');
          this.track('editor_opened');
          this.focusEditor();
        } catch (error) {
          this.setBusy(false);
          this.setState('idle');
          this.showError(error.message || this.dataset.errorMessage);
        }
      }

      focusEditor() {
        this.lastFocused = document.activeElement;
        this.closeBtn.focus();
      }

      /* -------------------------------------------------------------- callback */

      onMessage(event) {
        // Why the origin check here is not what the spec asked for.
        //
        // Shopify serves theme HTML assets under a response header of
        //   content-security-policy: sandbox allow-scripts allow-forms ...
        // with no allow-same-origin. That drops the callback document into an
        // opaque origin, so event.origin arrives as the string "null" and can
        // never be anything else, no matter which domain the asset URL used.
        //
        // So "reject origin null" is not achievable while the callback is a
        // theme asset. It is not a rule that was skipped, it is a rule that
        // this hosting cannot satisfy. An app proxy callback is served as a
        // normal first-party document and does get a real origin, which is why
        // it is required rather than merely tidier.
        //
        // What actually carries the weight in the meantime:
        //   * event.source identity. Only the iframe this element created can
        //     be the source, and that reference cannot be forged by another
        //     page or another frame.
        //   * the per-session nonce, which ties the message to this design.
        //
        // Residual risk to close with the app proxy: the ColorPark document
        // inside our own iframe is also a legitimate event.source and does
        // receive the nonce in its jumpUrl, so it could in principle assert a
        // queue ID we never verified. Only a server that checks the ID against
        // ColorPark can rule that out. Until then the operator matching a paid
        // order to a physical job is the real control.
        //
        // Through the app proxy none of that applies: the callback is a normal
        // first-party document, so demand the exact origin and refuse "null".
        const viaProxy = Boolean(this.sessionId);
        const originOk = viaProxy
          ? event.origin === this.callbackOrigin
          : event.origin === this.callbackOrigin || event.origin === 'null';
        if (!originOk) return;
        if (event.source !== this.frame.contentWindow) return;

        const msg = event.data;
        if (!msg || msg.source !== 'colorpark-complete' || !msg.queueId) return;
        // Proxy mode binds on the session the server issued; interim mode has
        // only the nonce we sent out with the jumpUrl.
        if (viaProxy ? msg.sessionId !== this.sessionId : msg.nonce !== this.nonce) return;
        if (this.state !== 'editing' && this.state !== 'recoverable_error') return;

        this.track('design_submitted');
        this.track('callback_received');

        // The server can say "this completion is wrong" and, until now, we did
        // not listen. `queue-id-conflict` means a SECOND printer job claimed a
        // design that already had one: the server keeps the first ID and flags
        // the clash, and we were reading only `msg.queueId`, calling finish(),
        // and showing the shopper a success screen. The result is a case that
        // gets printed with no order behind it, and nobody finds out.
        //
        // Anything the server flags is an exception, not a completion. Route it
        // to the recoverable path, which is the one that already knows how to
        // ask for a retry rather than claim the design is saved.
        if (msg.problem) {
          this.track('callback_problem_' + String(msg.problem).slice(0, 40));
          this.setState('recoverable_error');
          return;
        }

        this.finish(String(msg.queueId), msg.designImage);
      }

      /**
       * The artwork URL the proxy captured, re-checked here.
       *
       * The proxy already pinned it to img.colorpark.cn, so this is belt and
       * braces. It is worth the six lines: this value goes straight into an
       * <img src> on the cart and onto a paid order, and it arrived by
       * postMessage. Re-validating locally means a change on the other side of
       * that boundary cannot put an arbitrary host in front of our customers.
       *
       * Returns null for anything that is not plainly an https image on that
       * one host. A null simply means no thumbnail.
       */
      safeDesignImage(candidate) {
        if (typeof candidate !== 'string' || !candidate) return null;
        try {
          const u = new URL(candidate);
          if (u.protocol !== 'https:') return null;
          if (u.hostname !== 'img.colorpark.cn') return null;
          if (u.username || u.password) return null;
          u.search = '';
          return u.toString();
        } catch {
          return null;
        }
      }

      async finish(queueId, designImage) {
        this.pendingQueueId = queueId;
        this.pendingDesignImage = this.safeDesignImage(designImage);

        // Order matters: cover the ColorPark surface with our own saving layer
        // first, so the shopper never sees the printer queue page behind it.
        this.setState('saving');
        this.startWatchdog();

        try {
          await this.persistQueueId(queueId);
          const line = await this.verifyCartLine(queueId);
          if (!line) throw new Error('verification failed');

          this.clearWatchdog();
          this.track('queue_persisted');
          this.track('cart_verified');

          // Tell the server the cart association held. Best effort on purpose:
          // the shopper's design is already safe in the cart by this point, so
          // a failure here is an operator reconciliation problem, not a reason
          // to show them an error.
          if (this.sessionId) {
            fetch(`${this.cfg.proxyPath}/confirm`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: this.sessionId }),
            }).catch(() => {});
          }

          this.session = null;
          this.clearSession();
          this.resetFrame();
          this.setState('complete');
          this.track('completion_shown');
          this.root.querySelector('[data-cp-complete] a')?.focus();
          this.publishCartUpdate();
        } catch (error) {
          this.clearWatchdog();
          this.track('callback_failed');
          this.setState('recoverable_error');
          this.retryBtn?.focus();
        }
      }

      async persistQueueId(queueId) {
        // change.js replaces the whole properties object, so the existing ones
        // have to be read back and resent. Sending only the queue ID here wipes
        // the design token and with it the only durable handle on this line.
        const cart = await this.fetchCart();
        const line = cart.items.find(
          (i) => i.properties?._colorpark_design_token === this.session?.token
        );
        if (!line) throw new Error('cart line went missing');

        const properties = Object.assign({}, line.properties, {
          _colorpark_queue_id: queueId,
          _colorpark_status: 'submitted',
          Artwork: 'Your custom design is saved. Any sample product photo is for illustration only.',
        });

        // The design thumbnail, when the proxy managed to capture one.
        //
        // Written conditionally rather than as a possibly-null value, because
        // Shopify stores a null property as the literal string "null" and the
        // cart would then try to load that as an image. Absent means absent.
        //
        // This is NOT part of verifyCartLine's success test, on purpose. A
        // missing thumbnail is a cosmetic loss; failing verification over one
        // would strand a design the printer is already holding.
        if (this.pendingDesignImage) {
          properties._colorpark_design_image = this.pendingDesignImage;
          // Private properties are hidden by Shopify checkout and order status.
          // Carry a customer-visible reference as well as the production field.
          properties['Your design'] = this.pendingDesignImage;
        }

        const res = await fetch(this.cfg.cartChange, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
        });
        if (!res.ok) throw new Error('could not write the queue ID to the cart');
      }

      async verifyCartLine(queueId) {
        // Line keys change when properties change, so the design token, not the
        // key captured at add time, is what identifies the line afterwards.
        const cart = await this.fetchCart();
        const line = cart.items.find(
          (i) => i.properties?._colorpark_design_token === this.session?.token
        );
        if (!line) return null;

        const p = line.properties || {};
        const ok =
          p._colorpark_queue_id === queueId &&
          p._colorpark_status === 'submitted' &&
          p._colorpark_goods_id === this.cfg.goodsId &&
          p._colorpark_machine_id === this.cfg.machineId;
        return ok ? line : null;
      }

      async fetchCart() {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('could not read the cart');
        return res.json();
      }

      onRetry() {
        this.track('retry_started');
        if (!this.pendingQueueId) {
          this.setState('editing');
          return;
        }
        this.finish(this.pendingQueueId, this.pendingDesignImage).then(() => {
          if (this.state === 'complete') this.track('retry_succeeded');
        });
      }

      startWatchdog() {
        this.clearWatchdog();
        // A spinner that never resolves is worse than an error, because the
        // shopper cannot tell whether their design was kept.
        this.watchdog = window.setTimeout(() => {
          if (this.state !== 'saving') return;
          this.track('callback_failed');
          this.setState('recoverable_error');
          this.retryBtn?.focus();
        }, this.cfg.saveTimeout);
      }

      clearWatchdog() {
        if (this.watchdog) window.clearTimeout(this.watchdog);
        this.watchdog = null;
      }

      /* --------------------------------------------------------------- abandon */

      onKeydown(event) {
        if (event.key !== 'Escape') return;
        if (this.state === 'editing') this.onClose();
      }

      async onClose() {
        if (this.state === 'saving') return; // never interrupt a cart write

        if (this.state === 'complete' || this.state === 'recoverable_error') {
          this.setState('idle');
          this.restoreFocus();
          return;
        }

        if (!window.confirm(this.dataset.abandonConfirm)) return;

        this.track('editor_closed_abandoned');
        const session = this.session;
        this.session = null;
        this.clearSession();
        this.setState('abandoned');

        if (session?.lineKey) {
          try {
            if (this.cfg.abandon === 'remove') {
              await this.removeLine(session);
            } else {
              await this.markIncomplete(session);
            }
          } catch (error) {
            // Removal failed, so fall back to marking it. Either way the line
            // must not be able to reach checkout as a normal undesigned case.
            try {
              await this.markIncomplete(session);
            } catch (ignored) {
              /* the checkout validation function is the backstop */
            }
          }
          this.publishCartUpdate();
        }

        this.resetFrame();
        this.setState('idle');
        this.restoreFocus();
      }

      async removeLine(session) {
        const cart = await this.fetchCart();
        const line = cart.items.find(
          (i) => i.properties?._colorpark_design_token === session.token
        );
        if (!line) return;

        await fetch(this.cfg.cartChange, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
      }

      async markIncomplete(session) {
        const cart = await this.fetchCart();
        const line = cart.items.find(
          (i) => i.properties?._colorpark_design_token === session.token
        );
        if (!line) return;

        const properties = Object.assign({}, line.properties, {
          _colorpark_status: 'abandoned',
        });
        await fetch(this.cfg.cartChange, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
        });
      }

      restoreFocus() {
        if (this.lastFocused && document.contains(this.lastFocused)) this.lastFocused.focus();
        else this.button.focus();
      }

      resetFrame() {
        this.frame.removeAttribute('src');
        delete this.frame.dataset.loaded;
        this.frameReady = false;
        this.pendingQueueId = null;
        this.pendingDesignImage = null;
        // A new design is a new session and a new token. Carrying either over
        // would let a second design claim the first one's printer job.
        this.pendingToken = null;
        this.sessionId = null;
        this.syncLabel();
        window.setTimeout(() => this.warm(), 500);
      }

      /* -------------------------------------------------------------- variants */

      onVariantChange(variant) {
        // The variant is read live rather than captured at render time, so the
        // line that gets created is always the one the shopper is looking at.
        if (!variant) {
          this.variantAvailable = false;
          this.button.setAttribute('aria-disabled', 'true');
          return;
        }
        this.variantId = String(variant.id);
        this.variantAvailable = Boolean(variant.available);
        this.button.setAttribute('aria-disabled', this.variantAvailable ? 'false' : 'true');
        this.syncLabel();
      }

      /* --------------------------------------------------------------- session */

      persistSession() {
        // Recovery aid only. The cart line properties remain authoritative,
        // and the server record will be once the app proxy exists.
        try {
          sessionStorage.setItem(`colorpark:${this.cfg.goodsId}`, JSON.stringify(this.session));
        } catch (error) {
          /* private mode */
        }
      }

      clearSession() {
        try {
          sessionStorage.removeItem(`colorpark:${this.cfg.goodsId}`);
        } catch (error) {
          /* private mode */
        }
      }

      /* --------------------------------------------------------------- helpers */

      setBusy(busy) {
        this.button.setAttribute('aria-busy', String(busy));
        this.querySelector('[data-cp-label]').classList.toggle('hidden', busy);
        this.querySelector('[data-cp-loading]').classList.toggle('hidden', !busy);
      }

      clearError() {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      publishCartUpdate() {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'colorpark-customise',
            productVariantId: this.variantId,
          });
        }
      }

      track(event) {
        // Identifiers and timings only. Never artwork, never a cart token.
        const detail = {
          event: `colorpark_${event}`,
          goods_id: this.cfg.goodsId,
          machine_id: this.cfg.machineId,
          variant_id: this.variantId,
          design_token: this.session?.token || null,
          elapsed_ms: Math.round(performance.now()),
        };
        document.dispatchEvent(new CustomEvent('colorpark:track', { detail }));
        if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
      }
    }
  );
}
